import { usePage } from "@inertiajs/react";
import styled from "styled-components";

import {
  LandingLanguageProvider,
  useLandingLanguage,
} from "../../../contexts/landing-language";
import { categoryPath, insightsHome } from "../../../components/insights/paths";
import ArticleCard from "../../../components/insights/ArticleCard";
import Breadcrumb from "../../../components/insights/Breadcrumb";
import Footer from "../home/Footer";
import Header from "../home/Header";
import SeoHead from "../../../components/insights/SeoHead";
import ShareBar from "../../../components/insights/ShareBar";
import StrapiRichText from "../../../components/StrapiRichText";
import { formatDate } from "../../../components/insights/format";

const Page = styled.div`
  background: #fff;
  min-height: 100vh;
`;

const CrumbBar = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 128px 40px 0;

  @media (max-width: 768px) {
    padding: 112px 20px 0;
  }
`;

const Head = styled.header`
  max-width: 820px;
  margin: 0 auto;
  padding: 28px 24px 0;
  text-align: center;
`;

const Chip = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #c9a961;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: clamp(30px, 5vw, 46px);
  line-height: 1.15;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 18px;
`;

const Byline = styled.div`
  font-size: 14px;
  color: #8a8f9c;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const Dot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
`;

const Cover = styled.div`
  max-width: 1040px;
  margin: 36px auto 0;
  padding: 0 24px;

  img {
    width: 100%;
    border-radius: 16px;
    display: block;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
`;

const Body = styled.article`
  max-width: 720px;
  margin: 0 auto;
  padding: 44px 24px 8px;
  font-size: 17px;
  line-height: 1.8;
  color: #33373f;

  h2 {
    font-size: 26px;
    font-weight: 800;
    color: #1a1a1a;
    margin: 40px 0 14px;
    line-height: 1.25;
  }

  h3 {
    font-size: 20px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 30px 0 10px;
  }

  p {
    margin: 0 0 20px;
  }

  ul,
  ol {
    margin: 0 0 20px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: #b8933f;
    text-decoration: underline;
  }

  blockquote {
    margin: 28px 0;
    padding: 4px 0 4px 24px;
    border-left: 3px solid #c9a961;
    font-size: 20px;
    line-height: 1.6;
    font-style: italic;
    color: #1a1a1a;
  }

  figure {
    margin: 28px 0;
  }

  figure img {
    width: 100%;
    border-radius: 12px;
    display: block;
  }

  figcaption {
    margin-top: 8px;
    font-size: 13px;
    color: #8a8f9c;
    text-align: center;
  }
`;

const ShareWrap = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 16px 24px 8px;
`;

const RelatedSection = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 56px 40px 96px;

  @media (max-width: 768px) {
    padding: 48px 20px 80px;
  }
`;

const RelatedTitle = styled.h2`
  font-size: 24px;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0 0 24px;
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 22px;
`;

const InsightShow = () => {
  const { t, language } = useLandingLanguage();
  const { article, related, site_url: siteUrl } = usePage().props;
  const copy = t.insights;

  const path = `/insights/${article.slug}`;
  const canonical =
    (siteUrl || "").replace(/\/$/, "") +
    (language === "id" ? "/id" : "") +
    path;

  const crumbs = [
    {
      label: copy.breadcrumb.home,
      href: language === "id" ? "/id/hebe" : "/hebe",
    },
    { label: copy.breadcrumb.insights, href: insightsHome(language) },
  ];
  if (article.category) {
    crumbs.push({
      label: article.category.name,
      href: categoryPath(language, article.category.slug),
    });
  }
  crumbs.push({ label: article.title });

  const seo = article.seo || {};

  return (
    <Page>
      <SeoHead
        title={seo.meta_title || `${article.title} — HEBE Insights`}
        description={seo.meta_description || article.excerpt}
      />
      <Header forceScrolled />

      <CrumbBar>
        <Breadcrumb items={crumbs} />
      </CrumbBar>

      <Head>
        {article.category ? <Chip>{article.category.name}</Chip> : null}
        <Title>{article.title}</Title>
        <Byline>
          {article.author_name ? (
            <span>
              {copy.detail.by} {article.author_name}
            </span>
          ) : null}
          {article.published_date ? (
            <>
              <Dot />
              <span>{formatDate(article.published_date, language)}</span>
            </>
          ) : null}
          {article.reading_minutes ? (
            <>
              <Dot />
              <span>
                {article.reading_minutes} {copy.detail.minRead}
              </span>
            </>
          ) : null}
        </Byline>
      </Head>

      {article.cover_url ? (
        <Cover>
          <img src={article.cover_url} alt={article.title} />
        </Cover>
      ) : null}

      <Body>
        <StrapiRichText content={article.body} />
      </Body>

      <ShareWrap>
        <ShareBar url={canonical} title={article.title} />
      </ShareWrap>

      {related?.length ? (
        <RelatedSection>
          <RelatedTitle>{copy.detail.related}</RelatedTitle>
          <RelatedGrid>
            {related.map((item) => (
              <ArticleCard key={item.slug} article={item} />
            ))}
          </RelatedGrid>
        </RelatedSection>
      ) : null}

      <Footer />
    </Page>
  );
};

const InsightShowPage = () => {
  return (
    <LandingLanguageProvider>
      <InsightShow />
    </LandingLanguageProvider>
  );
};

export default InsightShowPage;
